import React, { useState, useRef, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import {
  FileText,
  Download,
  Printer,
  Plus,
  Trash2,
  Upload,
  RefreshCw,
  Building2,
  User,
  CreditCard,
  FileCheck,
  Receipt,
  CheckCircle
} from 'lucide-react';

// Indian States with Codes
const INDIAN_STATES = [
  { code: '01', name: 'Jammu & Kashmir' },
  { code: '02', name: 'Himachal Pradesh' },
  { code: '03', name: 'Punjab' },
  { code: '04', name: 'Chandigarh' },
  { code: '05', name: 'Uttarakhand' },
  { code: '06', name: 'Haryana' },
  { code: '07', name: 'Delhi' },
  { code: '08', name: 'Rajasthan' },
  { code: '09', name: 'Uttar Pradesh' },
  { code: '10', name: 'Bihar' },
  { code: '11', name: 'Sikkim' },
  { code: '12', name: 'Arunachal Pradesh' },
  { code: '13', name: 'Nagaland' },
  { code: '14', name: 'Manipur' },
  { code: '15', name: 'Mizoram' },
  { code: '16', name: 'Tripura' },
  { code: '17', name: 'Meghalaya' },
  { code: '18', name: 'Assam' },
  { code: '19', name: 'West Bengal' },
  { code: '20', name: 'Jharkhand' },
  { code: '21', name: 'Odisha' },
  { code: '22', name: 'Chhattisgarh' },
  { code: '23', name: 'Madhya Pradesh' },
  { code: '24', name: 'Gujarat' },
  { code: '26', name: 'Dadra & Nagar Haveli and Daman & Diu' },
  { code: '27', name: 'Maharashtra' },
  { code: '29', name: 'Karnataka' },
  { code: '30', name: 'Goa' },
  { code: '31', name: 'Lakshadweep' },
  { code: '32', name: 'Kerala' },
  { code: '33', name: 'Tamil Nadu' },
  { code: '34', name: 'Puducherry' },
  { code: '35', name: 'Andaman & Nicobar Islands' },
  { code: '36', name: 'Telangana' },
  { code: '37', name: 'Andhra Pradesh' },
  { code: '38', name: 'Ladakh' }
];

// Number to Words conversion (Indian format)
const numberToWords = (num) => {
  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  
  if (num === 0) return 'Zero';
  
  const convertLessThanThousand = (n) => {
    if (n === 0) return '';
    if (n < 20) return ones[n];
    if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
    return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' ' + convertLessThanThousand(n % 100) : '');
  };
  
  const convert = (n) => {
    if (n < 1000) return convertLessThanThousand(n);
    if (n < 100000) return convertLessThanThousand(Math.floor(n / 1000)) + ' Thousand' + (n % 1000 ? ' ' + convertLessThanThousand(n % 1000) : '');
    if (n < 10000000) return convertLessThanThousand(Math.floor(n / 100000)) + ' Lakh' + (n % 100000 ? ' ' + convert(n % 100000) : '');
    return convertLessThanThousand(Math.floor(n / 10000000)) + ' Crore' + (n % 10000000 ? ' ' + convert(n % 10000000) : '');
  };
  
  const rupees = Math.floor(num);
  const paise = Math.round((num - rupees) * 100);
  
  let words = convert(rupees) + ' Rupees';
  if (paise > 0) {
    words += ' and ' + convert(paise) + ' Paise';
  }
  return words + ' Only';
};

// Generate Invoice/Quotation Number
const generateNumber = (type) => {
  const year = new Date().getFullYear();
  const random = Math.floor(Math.random() * 900) + 100;
  return type === 'invoice' ? `INV-${year}-${random}` : `QTN-${year}-${random}`;
};

// Default data
const getDefaultData = () => ({
  type: 'invoice',
  number: generateNumber('invoice'),
  date: new Date().toISOString().split('T')[0],
  logo: null,
  themeColor: '#2563eb',
  
  seller: {
    name: 'Utkarshudyam Solution Pvt. Ltd.',
    address: 'Office 6, First Floor, Shivkrupa Residency, Vatva, Ahmedabad, Gujarat - 382440',
    gstEnabled: true,
    gstin: '24AADCU5701A1ZZ',
    phone: '9316085383',
    email: 'neelesh@utkarshudyam.com'
  },
  
  buyer: {
    name: '',
    gstin: '',
    billingAddress: '',
    shippingAddress: '',
    sameAsBilling: true,
    state: '24',
    phone: '',
    email: ''
  },
  
  sellerState: '24',
  
  items: [
    { description: '', hsn: '', qty: 1, rate: 0, gstPercent: 18 }
  ],
  
  payment: {
    status: 'not_received',
    mode: '',
    amountReceived: 0
  },
  
  bank: {
    show: true,
    bankName: 'HDFC Bank',
    accountNumber: '50200012345678',
    ifsc: 'HDFC0001234',
    upiId: 'utkarshudyam@hdfcbank',
    qrCode: null
  },
  
  terms: `1. Payment is due within 15 days of invoice date.
2. Please include invoice number in payment reference.
3. This is a computer generated invoice.
4. Subject to Ahmedabad jurisdiction.`
});

const InvoiceGenerator = () => {
  const [data, setData] = useState(getDefaultData());
  const [loading, setLoading] = useState(false);
  const invoiceRef = useRef(null);
  const logoInputRef = useRef(null);
  const qrInputRef = useRef(null);

  // Calculate totals
  const calculations = useCallback(() => {
    let subtotal = 0;
    let totalGst = 0;
    
    data.items.forEach(item => {
      const lineTotal = item.qty * item.rate;
      const gstAmount = (lineTotal * item.gstPercent) / 100;
      subtotal += lineTotal;
      totalGst += gstAmount;
    });
    
    const isSameState = data.sellerState === data.buyer.state;
    const cgst = isSameState ? totalGst / 2 : 0;
    const sgst = isSameState ? totalGst / 2 : 0;
    const igst = !isSameState ? totalGst : 0;
    const grandTotal = subtotal + totalGst;
    const amountReceived = parseFloat(data.payment.amountReceived) || 0;
    const dueAmount = grandTotal - amountReceived;
    const isPaid = data.payment.status === 'received' || (dueAmount <= 0 && data.payment.status === 'partial');
    
    return {
      subtotal,
      cgst,
      sgst,
      igst,
      totalGst,
      grandTotal,
      amountReceived,
      dueAmount: Math.max(0, dueAmount),
      isSameState,
      isPaid
    };
  }, [data.items, data.sellerState, data.buyer.state, data.payment.amountReceived, data.payment.status]);

  const calc = calculations();

  // Handle logo upload and color extraction
  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = async (event) => {
      const logoUrl = event.target.result;
      setData(prev => ({ ...prev, logo: logoUrl }));
      
      try {
        const img = new Image();
        img.crossOrigin = 'Anonymous';
        img.src = logoUrl;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          canvas.width = img.width;
          canvas.height = img.height;
          ctx.drawImage(img, 0, 0);
          
          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          const pixels = imageData.data;
          
          let r = 0, g = 0, b = 0, count = 0;
          for (let i = 0; i < pixels.length; i += 4) {
            if (pixels[i + 3] > 128) {
              r += pixels[i];
              g += pixels[i + 1];
              b += pixels[i + 2];
              count++;
            }
          }
          
          if (count > 0) {
            r = Math.round(r / count);
            g = Math.round(g / count);
            b = Math.round(b / count);
            const hex = '#' + [r, g, b].map(x => x.toString(16).padStart(2, '0')).join('');
            setData(prev => ({ ...prev, themeColor: hex }));
          }
        };
      } catch (err) {
        console.log('Color extraction failed');
      }
    };
    reader.readAsDataURL(file);
  };

  const handleQRUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      setData(prev => ({ ...prev, bank: { ...prev.bank, qrCode: event.target.result }}));
    };
    reader.readAsDataURL(file);
  };

  // Update handlers
  const updateSeller = (field, value) => setData(prev => ({ ...prev, seller: { ...prev.seller, [field]: value }}));
  const updateBuyer = (field, value) => setData(prev => ({ ...prev, buyer: { ...prev.buyer, [field]: value }}));
  const updateBank = (field, value) => setData(prev => ({ ...prev, bank: { ...prev.bank, [field]: value }}));
  const updatePayment = (field, value) => setData(prev => ({ ...prev, payment: { ...prev.payment, [field]: value }}));

  const updateItem = (index, field, value) => {
    const newItems = [...data.items];
    newItems[index] = { ...newItems[index], [field]: field === 'qty' || field === 'rate' || field === 'gstPercent' ? parseFloat(value) || 0 : value };
    setData(prev => ({ ...prev, items: newItems }));
  };

  const addItem = () => setData(prev => ({ ...prev, items: [...prev.items, { description: '', hsn: '', qty: 1, rate: 0, gstPercent: 18 }]}));

  const removeItem = (index) => {
    if (data.items.length === 1) { toast.error('At least one item required'); return; }
    setData(prev => ({ ...prev, items: prev.items.filter((_, i) => i !== index)}));
  };

  // Toggle Invoice/Quotation
  const setDocType = (type) => {
    setData(prev => ({ ...prev, type, number: generateNumber(type) }));
  };

  const resetForm = () => {
    if (window.confirm('Reset form?')) {
      setData(getDefaultData());
      toast.success('Form reset');
    }
  };

  const validateForm = () => {
    if (!data.seller.name) { toast.error('Seller name required'); return false; }
    if (!data.seller.phone) { toast.error('Seller phone required'); return false; }
    if (!data.seller.email) { toast.error('Seller email required'); return false; }
    if (!data.buyer.name) { toast.error('Buyer name required'); return false; }
    if (!data.buyer.billingAddress) { toast.error('Billing address required'); return false; }
    if (data.items.some(item => !item.description)) { toast.error('All items need description'); return false; }
    return true;
  };

  // Generate PDF - FIXED for proper A4 multi-page support
  const generatePDF = async () => {
    if (!validateForm()) return;
    setLoading(true);
    
    try {
      const html2pdf = (await import('html2pdf.js')).default;
      const element = invoiceRef.current;
      
      // Clone the element to avoid modifying original
      const clonedElement = element.cloneNode(true);
      
      // Create a temporary container
      const tempContainer = document.createElement('div');
      tempContainer.style.position = 'absolute';
      tempContainer.style.left = '-9999px';
      tempContainer.style.top = '0';
      tempContainer.appendChild(clonedElement);
      document.body.appendChild(tempContainer);
      
      const opt = {
        margin: [10, 10, 10, 10],
        filename: `${data.number}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { 
          scale: 2,
          useCORS: true,
          logging: false,
          letterRendering: true,
          windowWidth: 794,
          scrollX: 0,
          scrollY: 0
        },
        jsPDF: { 
          unit: 'mm', 
          format: 'a4', 
          orientation: 'portrait'
        },
        pagebreak: { 
          mode: ['avoid-all', 'css', 'legacy'],
          before: '.page-break-before',
          after: '.page-break-after',
          avoid: '.page-break-avoid'
        }
      };
      
      await html2pdf().set(opt).from(clonedElement).save();
      
      // Cleanup
      document.body.removeChild(tempContainer);
      
      toast.success('PDF downloaded successfully!');
    } catch (error) {
      toast.error('PDF generation failed');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Print function - FIXED
  const handlePrint = () => {
    if (!validateForm()) return;
    
    const printContent = invoiceRef.current.innerHTML;
    const printWindow = window.open('', '_blank', 'width=800,height=600');
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>${data.number}</title>
        <style>
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          
          body {
            font-family: 'Inter', 'Segoe UI', Arial, sans-serif;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
          
          @page {
            size: A4;
            margin: 10mm;
          }
          
          @media print {
            body {
              width: 210mm;
              margin: 0;
              padding: 0;
            }
            
            table { page-break-inside: auto; }
            tr { page-break-inside: avoid; page-break-after: auto; }
            thead { display: table-header-group; }
            tfoot { display: table-footer-group; }
            
            .page-break-avoid {
              page-break-inside: avoid;
            }
          }
          
          .invoice-container {
            width: 190mm;
            min-height: auto;
            padding: 5mm;
            margin: 0 auto;
            background: white;
          }
        </style>
      </head>
      <body>
        <div class="invoice-container">
          ${printContent}
        </div>
      </body>
      </html>
    `);
    
    printWindow.document.close();
    
    // Wait for content to load, then print
    setTimeout(() => {
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    }, 500);
  };

  // Generate Invoice Preview Content
  const InvoicePreviewContent = () => (
    <div 
      style={{ 
        width: '794px',
        minHeight: '1123px',
        padding: '40px',
        fontFamily: "'Inter', 'Segoe UI', Arial, sans-serif",
        fontSize: '12px',
        lineHeight: '1.5',
        color: '#333',
        backgroundColor: 'white',
        boxSizing: 'border-box'
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: `3px solid ${data.themeColor}`, paddingBottom: '15px', marginBottom: '20px' }}>
        <div style={{ maxWidth: '55%' }}>
          {data.logo && <img src={data.logo} alt="Logo" style={{ height: '55px', marginBottom: '10px', objectFit: 'contain' }} />}
          <div style={{ fontSize: '18px', fontWeight: 'bold', color: data.themeColor }}>{data.seller.name}</div>
          <div style={{ fontSize: '11px', color: '#555', marginTop: '6px', lineHeight: '1.6' }}>{data.seller.address}</div>
          <div style={{ fontSize: '11px', color: '#555', marginTop: '4px' }}>
            Phone: {data.seller.phone} | Email: {data.seller.email}
          </div>
          {data.seller.gstEnabled && data.seller.gstin && (
            <div style={{ fontSize: '11px', fontWeight: '600', marginTop: '6px' }}>GSTIN: {data.seller.gstin}</div>
          )}
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: '26px', fontWeight: 'bold', color: data.themeColor, letterSpacing: '1px' }}>
            {data.type === 'invoice' ? 'TAX INVOICE' : 'QUOTATION'}
          </div>
          {calc.isPaid && (
            <div style={{ 
              display: 'inline-block', 
              background: '#22c55e', 
              color: 'white', 
              padding: '4px 14px', 
              borderRadius: '4px', 
              fontSize: '12px', 
              fontWeight: 'bold', 
              marginTop: '8px' 
            }}>
              PAID
            </div>
          )}
          <div style={{ fontSize: '11px', marginTop: '12px', textAlign: 'right' }}>
            <div style={{ marginBottom: '4px' }}><strong>{data.type === 'invoice' ? 'Invoice' : 'Quotation'} No:</strong> {data.number}</div>
            <div style={{ marginBottom: '4px' }}><strong>Date:</strong> {new Date(data.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</div>
            <div style={{ marginTop: '8px' }}>
              <strong>Place of Supply:</strong><br/>
              <span style={{ color: '#555' }}>{INDIAN_STATES.find(s => s.code === data.buyer.state)?.name}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Bill To */}
      <div style={{ marginBottom: '20px' }} className="page-break-avoid">
        <div style={{ display: 'inline-block', background: data.themeColor, color: 'white', padding: '4px 12px', fontSize: '11px', fontWeight: '600', marginBottom: '8px' }}>
          BILL TO
        </div>
        <div style={{ background: '#f8f9fa', padding: '14px', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
          <div style={{ fontSize: '15px', fontWeight: '600', marginBottom: '4px' }}>{data.buyer.name}</div>
          {data.buyer.gstin && <div style={{ fontSize: '11px', marginBottom: '4px' }}>GSTIN: {data.buyer.gstin}</div>}
          <div style={{ fontSize: '11px', color: '#555', marginBottom: '4px' }}>{data.buyer.billingAddress}</div>
          {(data.buyer.phone || data.buyer.email) && (
            <div style={{ fontSize: '11px', color: '#555' }}>
              {data.buyer.phone && <span>Phone: {data.buyer.phone}</span>}
              {data.buyer.phone && data.buyer.email && <span> | </span>}
              {data.buyer.email && <span>Email: {data.buyer.email}</span>}
            </div>
          )}
        </div>
      </div>

      {/* Items Table */}
      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px' }}>
        <thead>
          <tr style={{ background: data.themeColor, color: 'white' }}>
            <th style={{ padding: '10px 8px', textAlign: 'left', fontSize: '11px', fontWeight: '600', width: '30px' }}>#</th>
            <th style={{ padding: '10px 8px', textAlign: 'left', fontSize: '11px', fontWeight: '600' }}>Description</th>
            <th style={{ padding: '10px 8px', textAlign: 'center', fontSize: '11px', fontWeight: '600', width: '80px' }}>HSN/SAC</th>
            <th style={{ padding: '10px 8px', textAlign: 'center', fontSize: '11px', fontWeight: '600', width: '50px' }}>Qty</th>
            <th style={{ padding: '10px 8px', textAlign: 'right', fontSize: '11px', fontWeight: '600', width: '90px' }}>Rate (Rs.)</th>
            <th style={{ padding: '10px 8px', textAlign: 'center', fontSize: '11px', fontWeight: '600', width: '60px' }}>GST %</th>
            <th style={{ padding: '10px 8px', textAlign: 'right', fontSize: '11px', fontWeight: '600', width: '100px' }}>Amount (Rs.)</th>
          </tr>
        </thead>
        <tbody>
          {data.items.map((item, idx) => (
            <tr key={idx} style={{ borderBottom: '1px solid #e5e7eb' }} className="page-break-avoid">
              <td style={{ padding: '10px 8px', fontSize: '11px', verticalAlign: 'top' }}>{idx + 1}</td>
              <td style={{ padding: '10px 8px', fontSize: '11px', verticalAlign: 'top' }}>{item.description}</td>
              <td style={{ padding: '10px 8px', textAlign: 'center', fontSize: '11px', verticalAlign: 'top' }}>{item.hsn}</td>
              <td style={{ padding: '10px 8px', textAlign: 'center', fontSize: '11px', verticalAlign: 'top' }}>{item.qty}</td>
              <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '11px', verticalAlign: 'top' }}>{item.rate.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
              <td style={{ padding: '10px 8px', textAlign: 'center', fontSize: '11px', verticalAlign: 'top' }}>{item.gstPercent}%</td>
              <td style={{ padding: '10px 8px', textAlign: 'right', fontSize: '11px', verticalAlign: 'top' }}>{(item.qty * item.rate).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Summary Section */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '20px' }} className="page-break-avoid">
        <div style={{ width: '280px', border: '1px solid #e5e7eb', borderRadius: '6px', overflow: 'hidden' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', borderBottom: '1px solid #e5e7eb', fontSize: '11px', background: '#fafafa' }}>
            <span>Subtotal:</span>
            <span>Rs. {calc.subtotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
          </div>
          {calc.isSameState ? (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', borderBottom: '1px solid #e5e7eb', fontSize: '11px' }}>
                <span>CGST:</span>
                <span>Rs. {calc.cgst.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', borderBottom: '1px solid #e5e7eb', fontSize: '11px' }}>
                <span>SGST:</span>
                <span>Rs. {calc.sgst.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
              </div>
            </>
          ) : (
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', borderBottom: '1px solid #e5e7eb', fontSize: '11px' }}>
              <span>IGST:</span>
              <span>Rs. {calc.igst.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 12px', fontWeight: 'bold', fontSize: '14px', background: data.themeColor, color: 'white' }}>
            <span>GRAND TOTAL:</span>
            <span>Rs. {calc.grandTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
          </div>
          
          {/* Payment Details in Summary */}
          {data.payment.status !== 'not_received' && (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', borderTop: '2px solid #e5e7eb', fontSize: '11px', background: '#f0fdf4', color: '#15803d' }}>
                <span>Amount Received ({data.payment.mode || 'N/A'}):</span>
                <span>Rs. {calc.amountReceived.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
              </div>
              {calc.dueAmount > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', fontSize: '12px', fontWeight: 'bold', background: '#fef2f2', color: '#dc2626' }}>
                  <span>DUE AMOUNT:</span>
                  <span>Rs. {calc.dueAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Amount in Words */}
      <div style={{ background: '#f8f9fa', padding: '12px 16px', borderRadius: '6px', marginBottom: '20px', fontSize: '11px', border: '1px solid #e5e7eb' }} className="page-break-avoid">
        <strong>Amount in Words:</strong> {numberToWords(calc.grandTotal)}
      </div>

      {/* Payment Status Banner */}
      {data.payment.status !== 'not_received' && data.payment.mode && (
        <div style={{ 
          background: calc.isPaid ? '#dcfce7' : '#fef9c3', 
          border: `1px solid ${calc.isPaid ? '#22c55e' : '#eab308'}`, 
          padding: '12px 16px', 
          borderRadius: '6px', 
          marginBottom: '20px', 
          fontSize: '12px' 
        }} className="page-break-avoid">
          <strong style={{ color: calc.isPaid ? '#15803d' : '#854d0e' }}>
            {calc.isPaid ? 'Payment Status: PAID' : 'Payment Status: PARTIAL'}
          </strong>
          <span style={{ marginLeft: '10px' }}>
            Rs. {calc.amountReceived.toLocaleString('en-IN')} received via {data.payment.mode}
          </span>
          {calc.dueAmount > 0 && (
            <span style={{ color: '#dc2626', fontWeight: 'bold', marginLeft: '15px' }}>
              | Balance Due: Rs. {calc.dueAmount.toLocaleString('en-IN')}
            </span>
          )}
        </div>
      )}

      {/* Bank Details */}
      {data.bank.show && (
        <div style={{ display: 'flex', gap: '25px', marginBottom: '20px' }} className="page-break-avoid">
          <div style={{ flex: 1 }}>
            <div style={{ display: 'inline-block', background: data.themeColor, color: 'white', padding: '4px 12px', fontSize: '11px', fontWeight: '600', marginBottom: '8px' }}>
              BANK DETAILS
            </div>
            <div style={{ fontSize: '11px', lineHeight: '1.8', background: '#f8f9fa', padding: '12px', borderRadius: '6px' }}>
              <div><strong>Bank Name:</strong> {data.bank.bankName}</div>
              <div><strong>Account No:</strong> {data.bank.accountNumber}</div>
              <div><strong>IFSC Code:</strong> {data.bank.ifsc}</div>
              {data.bank.upiId && <div><strong>UPI ID:</strong> {data.bank.upiId}</div>}
            </div>
          </div>
          {data.bank.qrCode && (
            <div style={{ textAlign: 'center' }}>
              <img src={data.bank.qrCode} alt="QR Code" style={{ width: '90px', height: '90px', border: '1px solid #e5e7eb', borderRadius: '6px' }} />
              <div style={{ fontSize: '9px', marginTop: '4px', color: '#666' }}>Scan to Pay</div>
            </div>
          )}
        </div>
      )}

      {/* Terms */}
      <div style={{ marginBottom: '25px' }} className="page-break-avoid">
        <div style={{ display: 'inline-block', background: data.themeColor, color: 'white', padding: '4px 12px', fontSize: '11px', fontWeight: '600', marginBottom: '8px' }}>
          TERMS & CONDITIONS
        </div>
        <div style={{ fontSize: '10px', color: '#666', whiteSpace: 'pre-line', lineHeight: '1.6', background: '#f8f9fa', padding: '12px', borderRadius: '6px' }}>
          {data.terms}
        </div>
      </div>

      {/* Footer / Signature */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', paddingTop: '20px', borderTop: '1px solid #e5e7eb' }} className="page-break-avoid">
        <div style={{ fontSize: '9px', color: '#999' }}>
          This is a computer generated {data.type === 'invoice' ? 'invoice' : 'quotation'}.
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: '11px', fontWeight: '600', marginBottom: '50px' }}>For {data.seller.name}</div>
          <div style={{ fontSize: '11px', borderTop: '1px solid #333', paddingTop: '6px', minWidth: '150px' }}>Authorized Signatory</div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />
      
      <main className="flex-1 container mx-auto px-4 py-8 mt-32">
        {/* Header */}
        <div className="flex flex-wrap justify-between items-center mb-6 gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-[#0A2463]">Invoice & Quotation Generator</h1>
            <p className="text-gray-600 text-sm mt-1">Create professional invoices and quotations</p>
          </div>
          <Button onClick={resetForm} variant="outline" size="sm" data-testid="reset-form-btn">
            <RefreshCw className="mr-2" size={16} /> Reset
          </Button>
        </div>

        {/* Invoice/Quotation Toggle - PROMINENT */}
        <Card className="mb-6 border-none shadow-lg" data-testid="document-type-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-center gap-2">
              <span className="text-sm font-medium text-gray-600 mr-2">Document Type:</span>
              <div className="inline-flex rounded-lg border-2 border-[#0A2463] overflow-hidden">
                <button
                  onClick={() => setDocType('invoice')}
                  data-testid="invoice-toggle-btn"
                  className={`px-6 py-2.5 font-semibold text-sm transition-all flex items-center ${
                    data.type === 'invoice' 
                      ? 'bg-[#0A2463] text-white' 
                      : 'bg-white text-[#0A2463] hover:bg-gray-50'
                  }`}
                >
                  <Receipt className="mr-2" size={16} />
                  INVOICE
                </button>
                <button
                  onClick={() => setDocType('quotation')}
                  data-testid="quotation-toggle-btn"
                  className={`px-6 py-2.5 font-semibold text-sm transition-all flex items-center ${
                    data.type === 'quotation' 
                      ? 'bg-[#0A2463] text-white' 
                      : 'bg-white text-[#0A2463] hover:bg-gray-50'
                  }`}
                >
                  <FileText className="mr-2" size={16} />
                  QUOTATION
                </button>
              </div>
            </div>
            <p className="text-center text-xs text-gray-500 mt-2">
              Current: <strong>{data.type === 'invoice' ? 'Tax Invoice' : 'Quotation'}</strong> - {data.number}
            </p>
          </CardContent>
        </Card>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Form Section */}
          <div className="space-y-4">
            {/* Document Details */}
            <Card className="border-none shadow-md">
              <CardHeader className="bg-[#0A2463] text-white py-3 px-4 rounded-t-lg">
                <CardTitle className="text-base flex items-center gap-2">
                  <FileText size={18} />
                  {data.type === 'invoice' ? 'Invoice' : 'Quotation'} Details
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">{data.type === 'invoice' ? 'Invoice' : 'Quotation'} Number</Label>
                    <Input 
                      value={data.number} 
                      onChange={(e) => setData(prev => ({ ...prev, number: e.target.value }))} 
                      className="h-9"
                      data-testid="document-number-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Date</Label>
                    <Input 
                      type="date" 
                      value={data.date} 
                      onChange={(e) => setData(prev => ({ ...prev, date: e.target.value }))} 
                      className="h-9"
                      data-testid="document-date-input"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Logo (Optional - auto detects theme color)</Label>
                  <div className="flex items-center gap-3 mt-1">
                    <input ref={logoInputRef} type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" />
                    <Button type="button" variant="outline" size="sm" onClick={() => logoInputRef.current?.click()} data-testid="upload-logo-btn">
                      <Upload className="mr-1" size={14} /> Upload Logo
                    </Button>
                    {data.logo && (
                      <div className="flex items-center gap-2">
                        <img src={data.logo} alt="Logo" className="h-8 object-contain" />
                        <div className="w-5 h-5 rounded border" style={{ backgroundColor: data.themeColor }} title={`Theme: ${data.themeColor}`} />
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Seller Details */}
            <Card className="border-none shadow-md">
              <CardHeader className="py-3 px-4">
                <CardTitle className="text-base flex items-center gap-2 text-[#0A2463]">
                  <Building2 size={18} /> Seller Details (Your Business)
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 space-y-3">
                <div>
                  <Label className="text-xs">Business Name *</Label>
                  <Input value={data.seller.name} onChange={(e) => updateSeller('name', e.target.value)} className="h-9" data-testid="seller-name-input" />
                </div>
                <div>
                  <Label className="text-xs">Address *</Label>
                  <Textarea value={data.seller.address} onChange={(e) => updateSeller('address', e.target.value)} rows={2} data-testid="seller-address-input" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Phone Number *</Label>
                    <Input value={data.seller.phone} onChange={(e) => updateSeller('phone', e.target.value)} className="h-9" placeholder="9876543210" data-testid="seller-phone-input" />
                  </div>
                  <div>
                    <Label className="text-xs">Email Address *</Label>
                    <Input value={data.seller.email} onChange={(e) => updateSeller('email', e.target.value)} className="h-9" placeholder="email@company.com" data-testid="seller-email-input" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Place of Supply (State)</Label>
                  <select value={data.sellerState} onChange={(e) => setData(prev => ({ ...prev, sellerState: e.target.value }))} className="w-full h-9 px-2 border rounded-md text-sm" data-testid="seller-state-select">
                    {INDIAN_STATES.map(s => <option key={s.code} value={s.code}>{s.code} - {s.name}</option>)}
                  </select>
                </div>
                <div className="flex items-center gap-2">
                  <input type="checkbox" id="gstEnabled" checked={data.seller.gstEnabled} onChange={(e) => updateSeller('gstEnabled', e.target.checked)} data-testid="gst-enabled-checkbox" />
                  <Label htmlFor="gstEnabled" className="text-xs">GST Registered</Label>
                </div>
                {data.seller.gstEnabled && (
                  <div>
                    <Label className="text-xs">GSTIN</Label>
                    <Input value={data.seller.gstin} onChange={(e) => updateSeller('gstin', e.target.value.toUpperCase())} className="h-9" placeholder="22AAAAA0000A1Z5" data-testid="seller-gstin-input" />
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Buyer Details */}
            <Card className="border-none shadow-md">
              <CardHeader className="py-3 px-4">
                <CardTitle className="text-base flex items-center gap-2 text-[#0A2463]">
                  <User size={18} /> Buyer Details (Customer)
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 space-y-3">
                <div>
                  <Label className="text-xs">Customer/Company Name *</Label>
                  <Input value={data.buyer.name} onChange={(e) => updateBuyer('name', e.target.value)} className="h-9" placeholder="Customer Name" data-testid="buyer-name-input" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Phone Number</Label>
                    <Input value={data.buyer.phone} onChange={(e) => updateBuyer('phone', e.target.value)} className="h-9" placeholder="Optional" data-testid="buyer-phone-input" />
                  </div>
                  <div>
                    <Label className="text-xs">Email Address</Label>
                    <Input value={data.buyer.email} onChange={(e) => updateBuyer('email', e.target.value)} className="h-9" placeholder="Optional" data-testid="buyer-email-input" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">GSTIN (if registered)</Label>
                  <Input value={data.buyer.gstin} onChange={(e) => updateBuyer('gstin', e.target.value.toUpperCase())} className="h-9" placeholder="Optional" data-testid="buyer-gstin-input" />
                </div>
                <div>
                  <Label className="text-xs">Place of Supply (Buyer State)</Label>
                  <select value={data.buyer.state} onChange={(e) => updateBuyer('state', e.target.value)} className="w-full h-9 px-2 border rounded-md text-sm" data-testid="buyer-state-select">
                    {INDIAN_STATES.map(s => <option key={s.code} value={s.code}>{s.code} - {s.name}</option>)}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    {data.sellerState === data.buyer.state 
                      ? '✓ Same state - CGST + SGST will apply' 
                      : '→ Different state - IGST will apply'}
                  </p>
                </div>
                <div>
                  <Label className="text-xs">Billing Address *</Label>
                  <Textarea value={data.buyer.billingAddress} onChange={(e) => updateBuyer('billingAddress', e.target.value)} rows={2} placeholder="Full address with pincode" data-testid="buyer-address-input" />
                </div>
                <div className="flex items-center gap-2">
                  <input type="checkbox" id="sameAsBilling" checked={data.buyer.sameAsBilling} onChange={(e) => updateBuyer('sameAsBilling', e.target.checked)} />
                  <Label htmlFor="sameAsBilling" className="text-xs">Shipping same as billing</Label>
                </div>
              </CardContent>
            </Card>

            {/* Items */}
            <Card className="border-none shadow-md">
              <CardHeader className="py-3 px-4 flex flex-row items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2 text-[#0A2463]">
                  <FileCheck size={18} /> Items / Services
                </CardTitle>
                <Button onClick={addItem} size="sm" className="bg-green-600 hover:bg-green-700 h-8" data-testid="add-item-btn">
                  <Plus size={14} className="mr-1" /> Add Item
                </Button>
              </CardHeader>
              <CardContent className="p-4 space-y-3">
                {data.items.map((item, index) => (
                  <div key={index} className="p-3 bg-gray-50 rounded-lg space-y-2 border border-gray-200" data-testid={`item-row-${index}`}>
                    <div className="flex justify-between items-center">
                      <Badge variant="outline">Item {index + 1}</Badge>
                      <Button onClick={() => removeItem(index)} size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-500 hover:text-red-700 hover:bg-red-50" data-testid={`remove-item-${index}-btn`}>
                        <Trash2 size={14} />
                      </Button>
                    </div>
                    <Input 
                      placeholder="Description / Service Name *" 
                      value={item.description} 
                      onChange={(e) => updateItem(index, 'description', e.target.value)} 
                      className="h-8 text-sm"
                      data-testid={`item-description-${index}`}
                    />
                    <div className="grid grid-cols-4 gap-2">
                      <Input placeholder="HSN/SAC" value={item.hsn} onChange={(e) => updateItem(index, 'hsn', e.target.value)} className="h-8 text-sm" data-testid={`item-hsn-${index}`} />
                      <Input type="number" placeholder="Qty" value={item.qty} onChange={(e) => updateItem(index, 'qty', e.target.value)} className="h-8 text-sm" min="1" data-testid={`item-qty-${index}`} />
                      <Input type="number" placeholder="Rate" value={item.rate} onChange={(e) => updateItem(index, 'rate', e.target.value)} className="h-8 text-sm" min="0" data-testid={`item-rate-${index}`} />
                      <select value={item.gstPercent} onChange={(e) => updateItem(index, 'gstPercent', e.target.value)} className="h-8 px-2 border rounded-md text-sm" data-testid={`item-gst-${index}`}>
                        <option value="0">0% GST</option>
                        <option value="5">5% GST</option>
                        <option value="12">12% GST</option>
                        <option value="18">18% GST</option>
                        <option value="28">28% GST</option>
                      </select>
                    </div>
                    <div className="text-right text-xs text-gray-600 bg-white px-2 py-1 rounded">
                      Amount: <span className="font-semibold">Rs. {(item.qty * item.rate).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                      <span className="ml-2 text-gray-400">+ GST Rs. {((item.qty * item.rate * item.gstPercent) / 100).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Payment */}
            <Card className="border-none shadow-md">
              <CardHeader className="py-3 px-4">
                <CardTitle className="text-base flex items-center gap-2 text-[#0A2463]">
                  <CreditCard size={18} /> Payment Details
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 space-y-3">
                <div>
                  <Label className="text-xs">Payment Status</Label>
                  <select value={data.payment.status} onChange={(e) => updatePayment('status', e.target.value)} className="w-full h-9 px-2 border rounded-md text-sm" data-testid="payment-status-select">
                    <option value="not_received">Not Received (Pending)</option>
                    <option value="partial">Partial Payment</option>
                    <option value="received">Full Payment Received</option>
                  </select>
                </div>
                
                {data.payment.status !== 'not_received' && (
                  <div className="p-3 bg-green-50 rounded-lg space-y-3 border border-green-200">
                    <div>
                      <Label className="text-xs text-green-700">Payment Mode *</Label>
                      <select value={data.payment.mode} onChange={(e) => updatePayment('mode', e.target.value)} className="w-full h-9 px-2 border rounded-md text-sm" data-testid="payment-mode-select">
                        <option value="">Select Payment Mode</option>
                        <option value="Cash">Cash</option>
                        <option value="UPI">UPI</option>
                        <option value="Bank Transfer">Bank Transfer / NEFT / RTGS</option>
                        <option value="Cheque">Cheque</option>
                        <option value="Card">Debit/Credit Card</option>
                        <option value="Online">Online Payment</option>
                      </select>
                    </div>
                    <div>
                      <Label className="text-xs text-green-700">Amount Received (Rs.) *</Label>
                      <Input 
                        type="number" 
                        value={data.payment.amountReceived} 
                        onChange={(e) => updatePayment('amountReceived', e.target.value)} 
                        className="h-9"
                        placeholder="Enter amount received"
                        data-testid="amount-received-input"
                      />
                    </div>
                    
                    {/* Live Calculation Display */}
                    <div className="bg-white p-2 rounded text-xs space-y-1">
                      <div className="flex justify-between">
                        <span>Total Amount:</span>
                        <span className="font-semibold">Rs. {calc.grandTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                      </div>
                      <div className="flex justify-between text-green-600">
                        <span>Amount Received:</span>
                        <span className="font-semibold">Rs. {calc.amountReceived.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                      </div>
                      <div className={`flex justify-between font-bold ${calc.dueAmount > 0 ? 'text-red-600' : 'text-green-600'}`}>
                        <span>Due Amount:</span>
                        <span>Rs. {calc.dueAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                      </div>
                      {calc.isPaid && (
                        <div className="text-center mt-2">
                          <Badge className="bg-green-600">
                            <CheckCircle size={12} className="mr-1" /> FULLY PAID
                          </Badge>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                <div className="border-t pt-3 mt-3">
                  <div className="flex items-center gap-2 mb-2">
                    <input type="checkbox" id="showBank" checked={data.bank.show} onChange={(e) => updateBank('show', e.target.checked)} data-testid="show-bank-checkbox" />
                    <Label htmlFor="showBank" className="text-xs">Show Bank Details on {data.type === 'invoice' ? 'Invoice' : 'Quotation'}</Label>
                  </div>
                  {data.bank.show && (
                    <div className="space-y-2 p-3 bg-gray-50 rounded-lg">
                      <div className="grid grid-cols-2 gap-2">
                        <Input placeholder="Bank Name" value={data.bank.bankName} onChange={(e) => updateBank('bankName', e.target.value)} className="h-8 text-sm" data-testid="bank-name-input" />
                        <Input placeholder="Account Number" value={data.bank.accountNumber} onChange={(e) => updateBank('accountNumber', e.target.value)} className="h-8 text-sm" data-testid="bank-account-input" />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <Input placeholder="IFSC Code" value={data.bank.ifsc} onChange={(e) => updateBank('ifsc', e.target.value.toUpperCase())} className="h-8 text-sm" data-testid="bank-ifsc-input" />
                        <Input placeholder="UPI ID" value={data.bank.upiId} onChange={(e) => updateBank('upiId', e.target.value)} className="h-8 text-sm" data-testid="bank-upi-input" />
                      </div>
                      <div className="flex items-center gap-2">
                        <input ref={qrInputRef} type="file" accept="image/*" onChange={handleQRUpload} className="hidden" />
                        <Button type="button" variant="outline" size="sm" onClick={() => qrInputRef.current?.click()} className="h-7 text-xs" data-testid="upload-qr-btn">
                          <Upload className="mr-1" size={12} /> Upload QR Code
                        </Button>
                        {data.bank.qrCode && <img src={data.bank.qrCode} alt="QR" className="h-10 w-10 rounded border" />}
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Terms */}
            <Card className="border-none shadow-md">
              <CardHeader className="py-3 px-4">
                <CardTitle className="text-base text-[#0A2463]">Terms & Conditions</CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                <Textarea value={data.terms} onChange={(e) => setData(prev => ({ ...prev, terms: e.target.value }))} rows={4} className="text-sm" data-testid="terms-input" />
              </CardContent>
            </Card>

            {/* Action Buttons */}
            <div className="grid grid-cols-2 gap-3">
              <Button onClick={generatePDF} disabled={loading} className="bg-green-600 hover:bg-green-700 h-12 text-base" data-testid="download-pdf-btn">
                <Download className="mr-2" size={20} />
                {loading ? 'Generating PDF...' : 'Download PDF'}
              </Button>
              <Button onClick={handlePrint} variant="outline" className="h-12 text-base" data-testid="print-btn">
                <Printer className="mr-2" size={20} /> Print
              </Button>
            </div>
          </div>

          {/* Preview Section */}
          <div className="lg:sticky lg:top-8 h-fit">
            <Card className="border-none shadow-xl overflow-hidden">
              <CardHeader className="py-3 px-4 bg-[#0A2463] text-white">
                <CardTitle className="text-base flex items-center gap-2">
                  <FileText size={18} />
                  Live Preview - {data.type === 'invoice' ? 'Tax Invoice' : 'Quotation'}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div 
                  className="overflow-auto bg-gray-200" 
                  style={{ maxHeight: '85vh' }}
                  data-testid="invoice-preview-container"
                >
                  <div 
                    ref={invoiceRef}
                    className="mx-auto my-4 shadow-lg"
                    style={{ transform: 'scale(0.85)', transformOrigin: 'top center' }}
                  >
                    <InvoicePreviewContent />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      <Footer />
      
      {/* Print-specific styles */}
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          #invoice-print-area, #invoice-print-area * {
            visibility: visible;
          }
          #invoice-print-area {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
};

export default InvoiceGenerator;
